/**
 * Report-quality issues surfaced by REPORT_QUALITY_ISSUES (AP-451).
 *
 * Local types — the FE boundary lint forbids `state/` slices from
 * depending on `features/*` modules.
 */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/state/store';

export type IssueSeverity = 'low' | 'medium' | 'high';

export interface QualityIssue {
  severity: IssueSeverity;
  section: string;
  message: string;
}

export interface QualityIssuesPayload {
  ok: boolean;
  study_uuid: string;
  issues: QualityIssue[];
  report_uuid?: string | null;
}

export interface QualityIssueReport {
  ok: boolean;
  studyUuid: string;
  reportUuid: string | null;
  issues: QualityIssue[];
  receivedAt: number;
}

interface CopilotQualityIssuesState {
  byStudy: Record<string, QualityIssueReport>;
}

const initialState: CopilotQualityIssuesState = { byStudy: {} };

const slice = createSlice({
  name: 'copilotQualityIssues',
  initialState,
  reducers: {
    issuesReceived: (state, action: PayloadAction<QualityIssuesPayload>) => {
      const p = action.payload;
      state.byStudy[p.study_uuid] = {
        ok: p.ok,
        studyUuid: p.study_uuid,
        reportUuid: p.report_uuid ?? null,
        issues: p.issues,
        receivedAt: Date.now(),
      };
    },
    issuesCleared: (state, action: PayloadAction<{ studyUuid: string }>) => {
      delete state.byStudy[action.payload.studyUuid];
    },
  },
});

export const { issuesReceived, issuesCleared } = slice.actions;

export const selectIssuesForStudy =
  (studyUuid: string | null | undefined) =>
  (state: RootState): QualityIssueReport | null =>
    studyUuid ? (state.copilotQualityIssues.byStudy[studyUuid] ?? null) : null;

export default slice.reducer;
